// PiecePreview - ピースのテストプレイ画面（spec.md §5.7）
// 旧ビルダープレビュー（features/builder/canvas/MapPreview.tsx）のレイアウトを忠実復元。
// 3D シーンを右に、Blockly フライアウトを左に配置。クリア＝全ジェム取得。
// 遷移元（?from=test|mine|public）でリアクション操作可否とログ記録を出し分ける。

/* eslint-disable react-hooks/exhaustive-deps */
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useRef, useMemo, useState, useEffect } from "react";
import * as Blockly from "blockly";
import throttle from "lodash-es/throttle";
import { Scene } from "@babylonjs/core/scene";
import { RootState } from "app/store";
import { Dialog } from "common/blockly";
import Constants from "common/constant";
import { useScale } from "common/utils";
import { ZoomPanel } from "features/courses/components";
import { MainScene } from "./scene/MainScene";
import { PreviewBlocklyWorkspace } from "./blockly";
import { PreviewMode, parsePreviewMode } from "./types";
import { useElapsedTime } from "./hooks/useElapsedTime";
import { PreviewSceneTitle, PreviewHintPanel } from "./components";

const TOP_OFFSET = 120 + 32;
const COMMAND_PANEL_HEIGHT = 82;
const DEFAULT_BG = `${Constants.assetHost}/assets/images/bg_scene.jpg`;

export const PiecePreview = () => {
  const { width, scale } = useScale();
  const [searchParams] = useSearchParams();
  const mode: PreviewMode = parsePreviewMode(searchParams.get("from"));
  const [event, setEvent] = useState("");
  const [reload, _setReload] = useState(false);
  const reloadRef = useRef(reload);
  const setReload = (value: boolean) => {
    reloadRef.current = value;
    _setReload(value);
  };
  const [gameStart, setGameStart] = useState(false);
  const [scene, setScene] = useState<Scene | null>(null);
  const [blocklyFlyoutWidth, setBlocklyFlyoutWidth] = useState(0);
  const [blocklyFlyoutHeight, setBlocklyFlyoutHeight] = useState(0);
  const [workspace, setWorkspace] = useState<Blockly.WorkspaceSvg>(null);

  // 背景はピース設定（URL）があれば優先、なければデフォルト画像
  const background = useSelector((state: RootState) => state.builder.background);

  // ゲーム HUD（Score/タイム）の状態。表示は上部の PreviewSceneTitle、
  // 更新はゲームロジック側（MainScene/GameScene）が行うためここで持つ
  const [totalGem, setTotalGem] = useState(0);
  const [gemScore, setGemScore] = useState(0);
  const { elapsedMs, start: timerStart, stop: timerStop, reset: timerReset } = useElapsedTime();

  const handleResize = useMemo(
    () => throttle(() => setReload(!reloadRef.current), 1000, { leading: false, trailing: true }),
    [],
  );
  useEffect(() => {
    handleResize();
  }, [scale]);

  useEffect(() => {
    if (workspace) {
      const flyout = workspace.getFlyout() as Blockly.Flyout;
      setBlocklyFlyoutWidth(flyout.getWidth() + 30 * scale);
      setBlocklyFlyoutHeight(flyout.getHeight());
    }
  }, [workspace, scale]);

  const handleHideChaff = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target && workspace) {
      let target = e.target as HTMLElement;
      while (target) {
        const className = target.getAttribute("class");
        if (className !== null && className.indexOf("blocklySelected") !== -1) {
          return;
        }
        target = target.parentElement;
      }
      workspace.hideChaff(true);
    }
  };

  return (
    <div onClick={handleHideChaff} className="flex-col-view w-full h-full bg-transparent">
      <img
        alt="ゲームの背景"
        draggable="false"
        src={background || DEFAULT_BG}
        onError={(e) => {
          // 設定された背景 URL が壊れていたらデフォルトに戻す
          const img = e.target as HTMLImageElement;
          if (img.src !== DEFAULT_BG) img.src = DEFAULT_BG;
        }}
        className="absolute top-0 left-0 w-full h-full object-cover"
      />

      <Dialog workspace={workspace} />

      <div className="flex-col-view items-start w-full h-full z-[1] opacity-100 pointer-events-none">
        <div className="flex-col-el w-full z-10" style={{ height: 80 * scale }}>
          <PreviewSceneTitle totalGem={totalGem} gemScore={gemScore} elapsedMs={elapsedMs} />
        </div>

        <div
          className="flex-col-view !flex-1 w-full h-full pointer-events-none"
          style={{ marginTop: 32 * scale }}
        >
          {blocklyFlyoutWidth > 0 && (
            <div
              className="flex-col-view !absolute h-full rounded-xl pointer-events-none"
              style={{
                left: blocklyFlyoutWidth + 100 * scale,
                width: width - blocklyFlyoutWidth - 100 * scale,
                padding: `0px ${10 * scale}px ${10 * scale}px`,
              }}
            >
              <MainScene
                mode={mode}
                reload={reload}
                setReload={setReload}
                scene={scene}
                gameStart={gameStart}
                setGameStart={setGameStart}
                setScene={setScene}
                event={event}
                workspace={workspace}
                setLoading={() => {}}
                setTotalGem={setTotalGem}
                setGemScore={setGemScore}
                timerStart={timerStart}
                timerStop={timerStop}
                timerReset={timerReset}
              />
            </div>
          )}
        </div>

        <div
          className="flex-col-view !absolute top-0 left-0 !flex-1 h-full w-full rounded-xl z-20 pointer-events-none"
          style={{ padding: `0px ${10 * scale}px ${17 * scale}px` }}
        >
          <PreviewBlocklyWorkspace
            gameStart={gameStart}
            topOffset={TOP_OFFSET}
            commandPanelHeight={COMMAND_PANEL_HEIGHT}
            workspace={workspace}
            setWorkspace={setWorkspace}
            flyoutWidth={blocklyFlyoutWidth}
            flyoutHeight={blocklyFlyoutHeight}
            setEvent={setEvent}
          />
        </div>
      </div>

      <ZoomPanel scene={scene} />

      <PreviewHintPanel />
    </div>
  );
};
